import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PanResponder, StyleSheet, Text, View, type LayoutChangeEvent } from 'react-native';
import Svg, { Path } from 'react-native-svg';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { SignatureData } from '@/lib/signature';

export type { SignatureData } from '@/lib/signature';

const r = (n: number) => Math.round(n * 10) / 10;

/**
 * Πεδίο όπου ο χρήστης ζωγραφίζει την υπογραφή του με το δάχτυλο. Επιστρέφει το
 * SVG path (JSON {d,w,h}) μέσω onChange, ή null όταν καθαριστεί.
 */
export function SignaturePad({
  onChange,
  height = 180,
}: {
  onChange: (data: SignatureData | null) => void;
  height?: number;
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [strokes, setStrokes] = useState<string[]>([]);
  const [current, setCurrent] = useState('');
  const size = useRef({ w: 0, h: height });
  const currentRef = useRef('');

  function emit(all: string[]) {
    const d = all.join(' ').trim();
    onChange(d ? { d, w: size.current.w, h: size.current.h } : null);
  }

  const responder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (e) => {
        const { locationX: x, locationY: y } = e.nativeEvent;
        currentRef.current = `M${r(x)},${r(y)}`;
        setCurrent(currentRef.current);
      },
      onPanResponderMove: (e) => {
        const { locationX: x, locationY: y } = e.nativeEvent;
        currentRef.current += ` L${r(x)},${r(y)}`;
        setCurrent(currentRef.current);
      },
      onPanResponderRelease: () => {
        if (!currentRef.current) return;
        const stroke = currentRef.current;
        currentRef.current = '';
        setCurrent('');
        setStrokes((prev) => {
          const next = [...prev, stroke];
          emit(next);
          return next;
        });
      },
    }),
  ).current;

  function onLayout(e: LayoutChangeEvent) {
    size.current = { w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height };
  }

  function clear() {
    currentRef.current = '';
    setCurrent('');
    setStrokes([]);
    onChange(null);
  }

  const empty = strokes.length === 0 && !current;

  return (
    <View style={styles.wrap}>
      <View style={[styles.pad, { height }]} onLayout={onLayout} {...responder.panHandlers}>
        <Svg width="100%" height="100%">
          {strokes.map((d, i) => (
            <Path key={i} d={d} stroke={colors.text} strokeWidth={2.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
          ))}
          {!!current && (
            <Path d={current} stroke={colors.text} strokeWidth={2.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </Svg>
        {empty && <Text style={styles.placeholder}>{t('profile.signaturePadPlaceholder')}</Text>}
      </View>
      <View style={styles.bar}>
        <View style={styles.line} />
        <Text style={styles.clear} onPress={clear} accessibilityRole="button">
          {t('common.clear')}
        </Text>
      </View>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    wrap: { gap: spacing.xs },
    pad: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
      justifyContent: 'center',
      alignItems: 'center',
    },
    placeholder: {
      position: 'absolute',
      color: colors.textMuted,
      fontSize: fontSize.md,
    },
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    line: {
      flex: 1,
      height: 1,
      backgroundColor: colors.border,
      marginRight: spacing.md,
    },
    clear: {
      color: colors.danger,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
  });
}
